// ─────────────────────────────────────────────────────────────────────────────
// Opportunities, CD's right-rail card. Populated from the band-shared
// opportunities blob (lib fetchOpportunities → /api/data/opportunities). Each row
// shows the type pill, a time/date stamp, the title + location, the invitee
// avatars (ring coloured by their response), and an Edit button. Empty: CD's
// .empty-note--card. The count tag shows the number of opportunities.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import type { JSX } from 'react';
import type { Opportunity, OpportunityInvitee } from '../../lib';
import { initials, shortDate } from './format';
import { PlusIcon } from './glyphs';
import { OpportunityModal } from './OpportunityModal';

export interface OpportunitiesProps {
  readonly opportunities: ReadonlyArray<Opportunity>;
  /**
   * Contact-id → avatar url (lib buildInviteeAvatarMap). Resolves a photo for
   * invitees that weren't stored with a baked-in avatar, the legacy app bakes
   * `avatarUrl` at pick time, so older opps have none and fall back to initials.
   */
  readonly avatars?: Record<string, string>;
  /** Refetch the dashboard after an opp add/edit/delete. Falls back to a hard
   *  reload when omitted so the card never shows stale data. */
  readonly onChanged?: () => void;
  /** Current user id, stamped as createdById on a newly-created opp. */
  readonly currentUserId?: string | number | null;
}

// A small rotating palette for invitee avatars (CD seeds --fill inline).
const AV_FILLS = ['#8B1A2B', '#1d4ed8', '#157a3a', '#6c6a6a', '#a82238'];

/** Map a response value → CD's ring class (is-accepted / is-declined / is-pending). */
function ringClass(resp: string | undefined): string {
  const r = String(resp || '').toLowerCase();
  if (r === 'in' || r === 'accepted' || r === 'yes') return 'is-accepted';
  if (r === 'pass' || r === 'declined' || r === 'no') return 'is-declined';
  return 'is-pending';
}

// time-12h 2026-07-05, ALWAYS a 12-hour clock (CLAUDE.md frontend conventions):
// a stored 24h "HH:MM" ("17:00") renders "5:00 PM"; already-12h / free-text
// strings pass through untouched. Local fmt12h-style helper (same pattern the
// mobile screens use), display-only, never rewrites the stored value.
function fmt12h(s: string | null | undefined): string {
  const str = String(s ?? '').trim();
  if (!str) return '';
  const m = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return str;
  const hh = parseInt(m[1], 10);
  if (isNaN(hh) || hh > 23) return str;
  const period = hh >= 12 ? 'PM' : 'AM';
  let h12 = hh % 12;
  if (h12 === 0) h12 = 12;
  return h12 + ':' + m[2] + ' ' + period;
}

// opp-expiry 2026-07-05, parse an opp clock string ("17:00" from the time
// input, or legacy "7:30 pm" free text) WITHOUT the legacy afternoon heuristic:
// no am/pm means the hours are already 24h. Returns minutes into the day.
function oppClockMin(time: string | null | undefined): number | null {
  const m = String(time ?? '').trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])?\.?m?\.?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mins = m[2] ? parseInt(m[2], 10) : 0;
  const ap = m[3] ? m[3].toLowerCase() : '';
  if (h > 23 || mins > 59) return null;
  if (ap === 'p' && h < 12) h += 12;
  if (ap === 'a' && h === 12) h = 0;
  return h * 60 + mins;
}

// opp-expiry 2026-07-05, DISPLAY-ONLY expiry: an opp with a time hides once
// its date+time (device-local, never UTC) is >1 hour in the past; a date-only
// opp hides the day after (date < today). Undated opps never expire. The stored
// opp is NEVER deleted or mutated, it still counts for history/calendar.
function oppExpired(opp: Opportunity, now: Date): boolean {
  const iso = String(opp.date || '').slice(0, 10);
  if (!iso) return false;
  const clock = oppClockMin(opp.time);
  if (clock != null) {
    const h = Math.floor(clock / 60);
    const mm = clock % 60;
    // Local-time construction ('T..' suffix, no trailing 'Z') so the device TZ wins.
    const at = new Date(iso + 'T' + ('0' + h).slice(-2) + ':' + ('0' + mm).slice(-2) + ':00');
    if (!isNaN(at.getTime())) return now.getTime() - at.getTime() > 3600000;
  }
  const day = new Date(iso + 'T12:00:00'); // noon dodges DST rollover
  if (isNaN(day.getTime())) return false;
  const todayMid = new Date(now);
  todayMid.setHours(0, 0, 0, 0);
  return day.getTime() < todayMid.getTime();
}

/** A human time/date stamp for the opp top row (time-12h 2026-07-05: 12-hour). */
function oppTime(opp: Opportunity): string {
  if (opp.date) {
    const d = shortDate(opp.date);
    return opp.time ? d + ' · ' + fmt12h(opp.time) : d;
  }
  return fmt12h(opp.time) || '';
}

/** Type label for the pill (title-cased opp type). */
function typeLabel(opp: Opportunity): string {
  const t = String(opp.type || 'Offer');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Clean title + location, de-duplicated. Stored titles sometimes already embed
 * the type and/or the full address (e.g. "Rehearsal · 241 Venture Cir, …"),
 * which, combined with a separate address line, rendered the text twice. Strip
 * a leading "<Type> · " (the pill already shows the type) and only show the
 * address separately when it actually adds information.
 */
function oppTitleParts(opp: Opportunity): { title: string; loc: string } {
  let title = String(opp.title || '').trim();
  const addr = String(opp.address || '').trim();
  const pfx = typeLabel(opp) + ' · ';
  if (title.toLowerCase().startsWith(pfx.toLowerCase())) title = title.slice(pfx.length).trim();
  if (!title) title = addr || 'Untitled';
  const loc = addr && !title.includes(addr) && !addr.includes(title) ? addr : '';
  return { title, loc };
}

function inviteeName(inv: OpportunityInvitee): string {
  return inv.name || inv.bandName || inv.email || '';
}

/**
 * Resolve an invitee's avatar url with the SAME precedence the legacy app uses
 * (dashboard-wiring.js inviteeAvatarHtml :1537):
 *   inv.avatarUrl || inv.avatar_url || inv.photoUrl || inv.photo_url
 * The picker stores `avatarUrl` on each invitee (resolved from the contacts/
 * members blob at :1773), so the persisted opportunity already carries it.
 */
function inviteeAvatarUrl(inv: OpportunityInvitee, avatars?: Record<string, string>): string {
  const baked = inv.avatarUrl || inv.avatar_url || inv.photoUrl || inv.photo_url || '';
  if (baked) return baked;
  // Fall back to the contacts lookup by any identifier the invitee carries
  // (user_id / userId / contactId / id / email), the same keys the picker
  // stamps (dashboard-wiring.js:1762-1772) and the contacts blob is keyed on.
  if (avatars) {
    for (const id of [inv.user_id, inv.userId, inv.contactId, inv.id, inv.email]) {
      if (id != null && id !== '') {
        const hit = avatars[String(id)];
        if (hit) return hit;
      }
    }
  }
  return '';
}

function inviteeKey(inv: OpportunityInvitee, i: number): string {
  return String(inv.user_id ?? inv.userId ?? inv.id ?? inv.contactId ?? inv.email ?? i);
}

/**
 * One invitee avatar inside CD's `.who__av` circle, preserving the status-ring
 * class (is-accepted / is-declined / is-pending). Mirrors legacy
 * inviteeAvatarHtml (dashboard-wiring.js:1536-1543): render the photo as an
 * <img> when a url exists, else an initials badge. The <img> falls back to the
 * initials on error so a broken url never shows a blank circle.
 */
function InviteeAvatar({
  inv,
  ring,
  fill,
  avatars,
}: {
  readonly inv: OpportunityInvitee;
  readonly ring: string;
  readonly fill: string;
  readonly avatars?: Record<string, string>;
}): JSX.Element {
  const name = inviteeName(inv);
  const url = inviteeAvatarUrl(inv, avatars);
  const [broken, setBroken] = useState(false);
  const showImg = !!url && !broken;

  return (
    <span
      className={'who__av ' + ring}
      style={{ ['--fill' as string]: fill }}
      title={name}
    >
      {showImg ? (
        <img
          className="who__av-img"
          src={url}
          alt=""
          onError={() => setBroken(true)}
        />
      ) : (
        initials(name)
      )}
    </span>
  );
}

export function Opportunities({ opportunities, avatars, onChanged, currentUserId }: OpportunitiesProps): JSX.Element {
  // opp-expiry 2026-07-05, display-only filter: passed opps drop out of the
  // card (and out of the count tag), but stay in the stored blob untouched.
  const now = new Date();
  const visible = opportunities.filter((o) => !oppExpired(o, now));
  const count = visible.length;
  // null = closed; { opp: null } = create; { opp } = edit.
  const [editor, setEditor] = useState<{ opp: Opportunity | null } | null>(null);
  const refresh = onChanged ?? (() => window.location.reload());

  return (
    <section className="card" id="opportunities-card">
      <header className="card__head">
        <h3 className="card__title">Opportunities</h3>
        <div className="head-actions">
          <span className="tag">{count}</span>
          <button
            className="addbtn"
            type="button"
            aria-label="Add opportunity"
            onClick={() => setEditor({ opp: null })}
          >
            <PlusIcon />
          </button>
        </div>
      </header>
      <div className="card__body" style={{ paddingTop: 14 }}>
        <div className="opp-scroll">
          {count === 0 ? (
            // ── EMPTY (dashboard-empty.html) ──
            <p className="empty-note empty-note--card">
              No opportunities yet.
              <br />
              <span>Tap + to log a show, tour, sync, or anything worth tracking.</span>
            </p>
          ) : (
            visible.map((opp) => {
              const invitees = opp.invitees || [];
              const { title, loc } = oppTitleParts(opp);
              return (
                <article className="opp" key={opp.id}>
                  <div className="opp__top">
                    <span className="type-pill">{typeLabel(opp)}</span>
                    <span className="opp__time">{oppTime(opp)}</span>
                  </div>
                  <p className="opp__title">
                    {title}
                    {loc && <span className="opp__loc-inline"> · {loc}</span>}
                  </p>
                  <div className="opp__foot-row">
                    <div className="opp__avatars">
                      {invitees.map((inv, i) => {
                        const key = inviteeKey(inv, i);
                        const resp = opp.responses ? opp.responses[key] : undefined;
                        return (
                          <InviteeAvatar
                            key={key}
                            inv={inv}
                            ring={ringClass(resp)}
                            fill={AV_FILLS[i % AV_FILLS.length]}
                            avatars={avatars}
                          />
                        );
                      })}
                    </div>
                    <button className="opp__edit" type="button" onClick={() => setEditor({ opp })}>
                      Edit
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>

      {editor && (
        <OpportunityModal
          opp={editor.opp}
          currentUserId={currentUserId}
          onClose={() => setEditor(null)}
          onChanged={refresh}
        />
      )}
    </section>
  );
}
